import {
  Button,
  CloseIcon,
  ConsentOrdersChildrenLivingArrangementsSchema,
  InitialDetails,
  LivingArrangementIcon,
  RadioSelect,
  TextInput,
  useNames
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import "definitions/Yup/yup-extended";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

interface IProps {
  formId: string;
  store: InitialDetails;
  onSubmit: (values: InitialDetails) => void;
}

export default function Children({ onSubmit, formId, store }: IProps) {
  const { handleSubmit, control, watch, reset } = useForm({
    mode: "onSubmit",
    defaultValues: store,
    resolver: yupResolver(ConsentOrdersChildrenLivingArrangementsSchema)
  });
  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: "children.live_with" // unique name for your Field Array
  });

  const {
    fields: spouseFields,
    append: spouseAppend,
    remove: spouseRemove
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: "children.live_with_at_spouse" // unique name for your Field Array
  });

  const { initialDetails } = useSelector(selectConsentOrdersBase);

  const { spouseFirstName } = useNames({ initialDetails });

  const appendLiveWith = () => {
    append({
      first_name: "",
      last_name: "",
      gender: "",
      age: "",
      relation_to_child: ""
    });
  };

  const appendLiveWithAtSpouse = () => {
    spouseAppend({
      first_name: "",
      last_name: "",
      gender: "",
      age: "",
      relation_to_child: ""
    });
  };

  const removeLiveWith = (idx: number) => {
    if (watch("children.live_with")?.length === 1 && idx === 0) {
      reset();
    } else {
      remove(idx);
    }
  };

  const removeLiveWithAtSpouse = (idx: number) => {
    if (watch("children.live_with_at_spouse")?.length === 1 && idx === 0) {
      reset();
    } else {
      spouseRemove(idx);
    }
  };

  useEffect(() => {
    if (watch("children.if_live_with_anyone") === "yes" && watch("children.live_with")?.length === 0) {
      append({
        first_name: "",
        last_name: "",
        gender: "",
        age: "",
        relation_to_child: ""
      });
    }
    // remove all entries
    if (watch("children.if_live_with_anyone") === "no" && watch("children.live_with")?.length > 0) {
      remove();
    }

    if (
      watch("children.if_live_with_anyone_at_spouse") === "yes" &&
      watch("children.live_with_at_spouse")?.length === 0
    ) {
      spouseAppend({
        first_name: "",
        last_name: "",
        gender: "",
        age: "",
        relation_to_child: ""
      });
    }
    // remove all entries
    if (watch("children.if_live_with_anyone_at_spouse") === "no" && watch("children.live_with_at_spouse")?.length > 0) {
      spouseRemove();
    }
  }, [watch()]);

  return (
    <form id={formId} className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-8 pb-8 border-b">
        <LivingArrangementIcon classes="w-20 h-16 mx-auto mb-5" />

        <h3 className="text-center">Children’s Living Arrangements</h3>
      </div>
      <RadioSelect
        title={
          <>
            Does anyone else live in <span className="underline">your</span>
            &nbsp;home when a child is living with you?
          </>
        }
        subTitle="Don't include the children mentioned in the previous section. Include other people like grandparents, new partners etc"
        control={control}
        name="children.if_live_with_anyone"
        id="children.if_live_with_anyone"
        options={["yes", "no"]}
      />

      {Array.isArray(fields) && fields.length > 0 ? (
        <>
          <p className="font-bold mb-4 text-sm">
            Enter the details of every person living at&nbsp;
            <span className="underline">your</span> house
          </p>
          {fields.map(({ id }, idx) => (
            <section key={id} className="bg-[#f2f2f2] p-5 rounded-[10px] space-y-5">
              <div
                className={classNames("flex items-center pb-5 border-b", {
                  "justify-between": idx !== 0,
                  "justify-start": idx === 0
                })}
              >
                <div className="w-full font-bold">Person {idx + 1}</div>
                {idx !== 0 ? (
                  <button
                    type="button"
                    className="transition-all hover:bg-red-400/20 p-2 rounded-lg"
                    onClick={() => removeLiveWith(idx)}
                  >
                    <CloseIcon strokeColor="text-red-500" title="remove person details" />
                  </button>
                ) : null}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  title="First Name"
                  control={control}
                  name={`children.live_with.${idx}.first_name`}
                  id={`children.live_with.${idx}.first_name`}
                  placeholder="First Name"
                  wrapClasses="col-span-1"
                  theme="dark"
                />
                <TextInput
                  title="Last Name"
                  control={control}
                  name={`children.live_with.${idx}.last_name`}
                  id={`children.live_with.${idx}.last_name`}
                  placeholder="Last Name"
                  wrapClasses="col-span-1"
                  theme="dark"
                />
              </div>

              <RadioSelect
                title="Gender"
                control={control}
                name={`children.live_with.${idx}.gender`}
                id={`children.live_with.${idx}.gender`}
                options={["male", "female"]}
                theme="dark"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  title="Age"
                  control={control}
                  name={`children.live_with.${idx}.age`}
                  id={`children.live_with.${idx}.age`}
                  placeholder="Age eg: 21"
                  wrapClasses="col-span-1"
                  theme="dark"
                />

                <TextInput
                  title="Relationship to child"
                  control={control}
                  name={`children.live_with.${idx}.relation_to_child`}
                  id={`children.live_with.${idx}.relation_to_child`}
                  placeholder="Eg Aunty"
                  wrapClasses="col-span-1"
                  theme="dark"
                />
              </div>
            </section>
          ))}
        </>
      ) : null}

      {watch("children.if_live_with_anyone") === "yes" ? (
        <Button
          className="flex items-center justify-center px-7 py-2 mx-auto mt-10 mb-20"
          onClick={appendLiveWith}
          type="button"

          // appearance="outline"
        >
          Add Another Person
        </Button>
      ) : null}

      <RadioSelect
        title={
          <>
            Does anyone else live in&nbsp;
            <span className="underline">{spouseFirstName}'s</span> home when a child is living with {spouseFirstName}?
          </>
        }
        subTitle="Don't include the children mentioned in the previous section. Include other people like grandparents, new partners etc"
        control={control}
        name="children.if_live_with_anyone_at_spouse"
        id="children.if_live_with_anyone_at_spouse"
        options={["yes", "no"]}
      />

      {Array.isArray(spouseFields) && spouseFields.length > 0
        ? spouseFields.map(({ id }, idx) => (
            <section key={id} className="bg-[#f2f2f2] p-5 rounded-[10px]  space-y-5">
              <div
                className={classNames("flex items-center pb-5 border-b", {
                  "justify-between": idx !== 0,
                  "justify-start": idx === 0
                })}
              >
                <div className="w-full font-bold">Person {idx + 1}</div>
                {idx !== 0 ? (
                  <button
                    type="button"
                    className="transition-all hover:bg-red-400/20 p-2 rounded-lg"
                    onClick={() => removeLiveWithAtSpouse(idx)}
                  >
                    <CloseIcon strokeColor="text-red-500" title="remove person details" />
                  </button>
                ) : null}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  title="First Name"
                  control={control}
                  name={`children.live_with_at_spouse.${idx}.first_name`}
                  id={`children.live_with_at_spouse.${idx}.first_name`}
                  placeholder="First Name"
                  wrapClasses="col-span-1"
                  theme="dark"
                />
                <TextInput
                  title="Last Name"
                  control={control}
                  name={`children.live_with_at_spouse.${idx}.last_name`}
                  id={`children.live_with_at_spouse.${idx}.last_name`}
                  placeholder="Last Name"
                  wrapClasses="col-span-1"
                  theme="dark"
                />
              </div>

              <RadioSelect
                title="Gender"
                control={control}
                name={`children.live_with_at_spouse.${idx}.gender`}
                id={`children.live_with_at_spouse.${idx}.gender`}
                options={["male", "female"]}
                theme="dark"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  title="Age"
                  control={control}
                  name={`children.live_with_at_spouse.${idx}.age`}
                  id={`children.live_with_at_spouse.${idx}.age`}
                  placeholder="Age eg: 21"
                  wrapClasses="col-span-1"
                  theme="dark"
                />

                <TextInput
                  title="Relationship to child"
                  control={control}
                  name={`children.live_with_at_spouse.${idx}.relation_to_child`}
                  id={`children.live_with_at_spouse.${idx}.relation_to_child`}
                  placeholder="Eg Uncle"
                  wrapClasses="col-span-1"
                  theme="dark"
                />
              </div>
            </section>
          ))
        : null}

      {watch("children.if_live_with_anyone_at_spouse") === "yes" ? (
        <Button
          className="flex items-center justify-center px-7 py-2 mx-auto mt-10 mb-20"
          onClick={appendLiveWithAtSpouse}
          type="button"
          // appearance="outline"
        >
          Add Another Person
        </Button>
      ) : null}
    </form>
  );
}
